import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FeedbackIcon from '@mui/icons-material/Feedback';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttendanceReportContent from './AttendanceReportContent';
import FeedbackReportContent from './FeedbackReportContent';
import TrendReportContent from './TrendReportContent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `reports-tab-${index}`,
    'aria-controls': `reports-tabpanel-${index}`,
  };
}

const ReportsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'feedback' ? 1 : tabParam === 'trends' ? 2 : 0;
  const [tabValue, setTabValue] = useState(initialTab);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const tabNames = ['attendance', 'feedback', 'trends'];
    setSearchParams({ tab: tabNames[newValue] });
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View attendance, feedback, and trend reports for your events
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="report tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
            },
            '& .Mui-selected': {
              color: '#00b7ed',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#00b7ed',
            },
          }}
        >
          <Tab
            icon={<AssessmentIcon />}
            iconPosition="start"
            label="Attendance"
            {...a11yProps(0)}
          />
          <Tab
            icon={<FeedbackIcon />}
            iconPosition="start"
            label="Feedback"
            {...a11yProps(1)}
          />
          <Tab
            icon={<TrendingUpIcon />}
            iconPosition="start"
            label="Trends"
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <AttendanceReportContent />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <FeedbackReportContent />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <TrendReportContent />
      </TabPanel>
    </Box>
  );
};

export default ReportsPage;
